/**
 * What a refused publish says to the author (CONTENT_MODEL_SPEC §2.5).
 *
 * The publish itself is no longer a shape this module builds. A scene
 * published from the content panel is created the way every library asset is
 * — an empty row, the new bytes one at a time, then the document alone
 * (`create-asset-folder.ts`, §2.6) — and the `.glz` bundle that used to stand
 * in for it is back to being what §4 says it is: the download and
 * import-from-disk format, not the editor's write path.
 *
 * What is left is the half that has no other home: turning the server's 400
 * into a sentence an author can act on, which is the same job whichever
 * write was refused — a create from the content panel or a save back to the
 * open asset.
 */

/**
 * What to show an author when a publish is refused.
 *
 * A folder write that fails the server's document gate answers 400 with the
 * reason in the response body — which `files[]` entry it refused and why
 * (spec §2.5) — while the Axios error's own `message` is only
 * "Request failed with status code 400". Showing the latter tells an author
 * nothing about the reference they have to fix, so the body wins when there
 * is one.
 */
export function publishErrorMessage(error: unknown): string {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
        // The API's error envelopes, in the order they carry a human sentence.
        const body = data as { message?: unknown; title?: unknown; detail?: unknown; errors?: unknown };
        for (const field of [body.message, body.detail, body.title]) {
            if (typeof field === 'string' && field.trim()) return field;
        }
        // ASP.NET model-binding failures: { errors: { Field: ["..."] } }.
        if (body.errors && typeof body.errors === 'object') {
            const messages = Object.values(body.errors as Record<string, unknown>)
                .flatMap(value => Array.isArray(value) ? value : [value])
                .filter((value): value is string => typeof value === 'string');
            if (messages.length > 0) return messages.join('; ');
        }
    }
    return error instanceof Error ? error.message : String(error);
}

/**
 * The `files: [{ index, authored, stored }]` mapping a name mismatch answers
 * with (§2.4), rendered for a human.
 *
 * This is the one 400 whose body is a fix rather than a complaint: `stored` is
 * the uri the matching part actually landed under, so the repair is to repoint
 * those slots and send the same document again. Worth spelling out, because
 * reaching it at all means the repoint `publishAssetFolder` does from what
 * `POST /files` returned did not cover every entry the document names.
 */
export function nameMismatchDetail(error: unknown): string | undefined {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    const entries = (data as { files?: unknown } | undefined)?.files;
    if (!Array.isArray(entries) || entries.length === 0) return undefined;

    const lines = entries.map((entry: unknown) => {
        const { index, authored, stored } = (entry ?? {}) as Record<string, unknown>;
        const minted = typeof stored === 'string' && stored ? stored : '(no matching part)';
        return `  files[${String(index)}]: authored ${String(authored)} → stored ${minted}`;
    });
    return `The folder does not hold the names this document uses:\n${lines.join('\n')}`;
}
